import { hasOwnProperty, withDefault } from "collection-utils";
import styled from "styled-components";
import twemoji from "twemoji";
import { systemFontFamily } from "../BaseTheme";
import { massageImageUrl } from "../components/portable-renderers";
import type { IconImage } from "../components/SerializedApp";
import { logError } from "../debug-print";
import { IconWrapper, ImageStyle } from "./style";

export interface IconSize {
    filename: string;
    size: number;
}

export const expoIconSizes: IconSize[] = [
    { filename: "AppIcon20x20@2x.png", size: 20 * 2 },
    { filename: "AppIcon20x20@3x.png", size: 20 * 3 },
    { filename: "AppIcon29x29@2x.png", size: 29 * 2 },
    { filename: "AppIcon29x29@3x.png", size: 29 * 3 },
    { filename: "AppIcon40x40@2x.png", size: 40 * 2 },
    { filename: "AppIcon40x40@3x.png", size: 40 * 3 },
    { filename: "AppIcon60x60@2x.png", size: 60 * 2 },
    { filename: "AppIcon60x60@3x.png", size: 60 * 3 },
    { filename: "AppIcon76x76@2x~ipad.png", size: 76 * 2 },
    { filename: "AppIcon76x76~ipad.png", size: 76 },
    { filename: "AppIcon83.5x83.5@2x~ipad.png", size: 83.5 * 2 },
    { filename: "AppIcon1024x1024.png", size: 1024 },
];

export const iconsSizes: IconSize[] = [
    { filename: "iPhone 3x.png", size: 180 },
    { filename: "iPhone 2x.png", size: 120 },
    { filename: "iPad Pro.png", size: 167 },
    { filename: "iPad, iPad mini.png", size: 152 },
    { filename: "App Store.png", size: 1024 },
];

interface IconProps {
    image?: IconImage;
    size: number;
    color: string;
    clipCorners: boolean;
    loading?: boolean;
    pwaSafeArea?: boolean;
    onClick?(): void;
    pointerOnHover?: boolean;
    omitShadow?: boolean;
    lightGradientOverlay?: boolean;
    inset?: boolean;
    rounding?: number;
}

const LoadingLabel = styled.div`
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    font-family: ${systemFontFamily};
    font-weight: 500;
    font-size: 16px;
    text-align: center;
    color: white;
`;

const imgTagRegex = /src="(http[^"]+)"/;
const bombURL = "https://raw.githubusercontent.com/twitter/twemoji/gh-pages/2/svg/1f4a3.svg?sanitize=true";

export function Icon({
    size,
    color,
    image,
    clipCorners,
    onClick,
    loading,
    pointerOnHover,
    pwaSafeArea,
    omitShadow,
    rounding,
    lightGradientOverlay = false,
    inset = false,
}: IconProps): JSX.Element {
    const innerSizeRatio = pwaSafeArea ? 9 / 16 : 0.75;
    const svgSize = Math.round(size * innerSizeRatio);
    const roundingSize = rounding ? rounding : size / 4.363636364;

    if (loading === undefined) loading = false;

    const svgContainerStyle: React.CSSProperties = {
        position: "absolute",
        transform: "translateX(-50%) translateY(-50%)",
        width: svgSize,
        height: svgSize,
        top: "50%",
        left: "50%",
    };

    let withContainer: boolean = false;
    let content: JSX.Element | null = null;
    if (loading || image === undefined) {
        content = <LoadingLabel>{loading ? "Uploading…" : ""}</LoadingLabel>;
    } else {
        let url: string;

        if (hasOwnProperty(image, "url")) {
            url =
                massageImageUrl(image.url, { thumbnail: false, width: size * 2, height: size * 2 }, undefined) ??
                image.url;
        } else {
            if (hasOwnProperty(image, "emoji")) {
                try {
                    const imgTag = twemoji.parse(image.emoji, { folder: "svg", ext: ".svg" }) as string;
                    const match = imgTag.match(imgTagRegex);
                    url = match === null ? bombURL : match[1];
                } catch {
                    url = bombURL;
                }
            } else {
                logError("Icon image not recognized", image);
                url = "https://twemoji.maxcdn.com/2/svg/1f4a3.svg";
            }
            withContainer = true;
        }

        content = (
            <ImageStyle
                roundingSize={roundingSize}
                clipped={!withContainer && clipCorners}
                src={url}
                padding={inset ? 14 : undefined}
                lightGradientOverlay={lightGradientOverlay}
            />
        );
        if (withContainer) {
            content = <div style={svgContainerStyle}>{content}</div>;
        }
    }

    return (
        <IconWrapper
            size={size}
            roundingSize={roundingSize}
            background={color}
            clipped={clipCorners}
            onClick={onClick}
            omitShadow={omitShadow === true}
            showPointer={withDefault(pointerOnHover, false)}>
            {content}
        </IconWrapper>
    );
}
